import { DEFAULT_TIMEZONE, SNOOZE_MODES, type SnoozeMode } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Input, Segmented } from "@sagot/ui";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import type { SnoozeStep } from "../types";
import { DurationField } from "./duration-field";
import type { FlowContext } from "./flow-context";
import { PathSelect } from "./path-select";
import { StepChannelNotes } from "./channel-notes";
import { snoozeModeOf, todayText } from "./reply-model";
import { type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";
const NINE_AM = "09:00";

/** The step with only the chosen way of snoozing set (a snooze has exactly one). */
function withMode(step: SnoozeStep, mode: SnoozeMode): SnoozeStep {
  const cleared: SnoozeStep = { ...step, for: undefined, until: undefined, until_office_hours: undefined };
  if (mode === "until") return { ...cleared, until: { date: todayText(), time: NINE_AM } };
  if (mode === "until_office_hours") return { ...cleared, until_office_hours: true };
  return { ...cleared, for: { value: 1, unit: "hours" } };
}

/** Snooze: hide the conversation in the Inbox's Snoozed view for a while, until a time, or until the office opens. */
export function SnoozeForm({ step, disabled, onChange, paths, canAddPath }: FormProps<SnoozeStep> & Pick<FlowContext, "paths" | "canAddPath">) {
  const { t } = useTranslation();
  const zone = useWorkspaceDetails().data?.timezone ?? DEFAULT_TIMEZONE;
  const mode = snoozeModeOf(step);
  return (
    <div className="space-y-5">
      <p className="text-xs text-fg-subtle">{t(`${F}.snoozeIntro`)}</p>
      <div>
        <span className="label">{t(`${F}.snoozeMode`)}</span>
        <Segmented
          aria-label={t(`${F}.snoozeMode`)}
          value={mode}
          onValueChange={(next: SnoozeMode) => { if (next !== mode) onChange(withMode(step, next)); }}
          options={SNOOZE_MODES.map((option) => ({ value: option, label: t(`${F}.snoozeModes.${option}`) }))}
        />
      </div>
      {mode === "for" && step.for ? <DurationField legend={t(`${F}.snoozeFor`)} duration={step.for} disabled={disabled} onChange={(next) => { onChange({ ...step, for: next }); }} /> : null}
      {mode === "until" && step.until ? (
        <fieldset className="space-y-1.5">
          <legend className="label">{t(`${F}.snoozeUntil`)}</legend>
          <div className="flex flex-wrap gap-2">
            <Input type="date" aria-label={t(`${F}.snoozeDate`)} className="w-44" disabled={disabled} value={step.until.date} onChange={(e) => { onChange({ ...step, until: { ...step.until, date: e.target.value, time: step.until?.time ?? NINE_AM } }); }} />
            <Input type="time" aria-label={t(`${F}.snoozeTime`)} className="w-32" disabled={disabled} value={step.until.time} onChange={(e) => { onChange({ ...step, until: { ...step.until, time: e.target.value, date: step.until?.date ?? todayText() } }); }} />
          </div>
          <p className="hint">{t(`${F}.snoozeZone`, { zone })}</p>
        </fieldset>
      ) : null}
      {mode === "until_office_hours" ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${F}.snoozeOfficeHint`)}</p> : null}
      <PathSelect label={t(`${F}.wokeGoTo`)} hint={t(`${F}.wokeHint`)} value={step.woke_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.nextStep`)} disabled={disabled} onChange={(woke_goto) => { onChange({ ...step, woke_goto }); }} />
      <PathSelect label={t(`${F}.snoozeInterruptedGoTo`)} hint={t(`${F}.snoozeInterruptedHint`)} value={step.interrupted_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.endHere`)} disabled={disabled} onChange={(interrupted_goto) => { onChange({ ...step, interrupted_goto }); }} />
      <StepChannelNotes type="snooze" />
    </div>
  );
}
